export type CrewOwnershipContext = {
  roleId: number
  roleEntityId?: number | null
  companyGroupAdminId?: number | null
  companyAdminId?: number | null
  isSuperAdmin?: boolean
  effectiveBehaviorType?: string | null
  operatorCompanyGroupAdminId?: number | null
  operatorCompanyAdminId?: number | null
  selectedCompanyGroupAdminId?: number | null
  selectedCompanyAdminId?: number | null
}

export type ResolvedCrewOwnership = {
  companyGroupAdminId: number
  companyAdminId: number | null
}

const positiveId = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

const normalizeBehavior = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toUpperCase()

/**
 * Canonical crew ownership for create/update payloads.
 * Scoped actors never copy company-group IDs into companyAdminId.
 */
export function resolveCrewCreateOwnership(ctx: CrewOwnershipContext): ResolvedCrewOwnership | null {
  const {
    roleId,
    roleEntityId,
    companyGroupAdminId,
    companyAdminId,
    isSuperAdmin,
    effectiveBehaviorType,
    operatorCompanyGroupAdminId,
    operatorCompanyAdminId,
    selectedCompanyGroupAdminId,
    selectedCompanyAdminId,
  } = ctx

  const behavior = normalizeBehavior(effectiveBehaviorType)
  const isSuperAdminActor =
    roleId === 1 || isSuperAdmin === true || behavior === 'SUPER_ADMIN'
  const isGroupAdminActor = roleId === 5 || behavior === 'COMPANY_GROUP_ADMIN'
  const isCompanyAdminActor = roleId === 2 || behavior === 'COMPANY_ADMIN'

  const selectedCa = positiveId(selectedCompanyAdminId)
  const selectedCga = positiveId(selectedCompanyGroupAdminId)

  const operatorActsLikeCompanyAdmin =
    (roleId === 6 || behavior === 'OPERATOR') && positiveId(operatorCompanyAdminId) != null
  const operatorActsLikeGroupAdmin =
    (roleId === 6 || behavior === 'OPERATOR') &&
    !operatorActsLikeCompanyAdmin &&
    positiveId(operatorCompanyGroupAdminId) != null

  if (
    isSuperAdminActor ||
    (roleId === 6 && !operatorActsLikeCompanyAdmin && !operatorActsLikeGroupAdmin)
  ) {
    const cga = selectedCga
    if (!cga) return null
    const ca = selectedCa
    if (ca != null && ca === cga) {
      return {companyGroupAdminId: cga, companyAdminId: null}
    }
    return {companyGroupAdminId: cga, companyAdminId: ca}
  }

  if (isGroupAdminActor || operatorActsLikeGroupAdmin) {
    const cga =
      positiveId(companyGroupAdminId) ??
      positiveId(operatorActsLikeGroupAdmin ? operatorCompanyGroupAdminId : null) ??
      positiveId(roleId === 5 ? roleEntityId : null)
    if (!cga) return null
    const ca = selectedCa
    if (ca != null && ca === cga) {
      return {companyGroupAdminId: cga, companyAdminId: null}
    }
    return {companyGroupAdminId: cga, companyAdminId: ca}
  }

  if (isCompanyAdminActor || operatorActsLikeCompanyAdmin) {
    const ca = positiveId(companyAdminId) ??
      positiveId(operatorCompanyAdminId) ??
      positiveId(roleId === 2 ? selectedCompanyAdminId : null)
    const cga =
      positiveId(companyGroupAdminId) ??
      positiveId(operatorCompanyGroupAdminId) ??
      positiveId(roleId === 2 ? roleEntityId : null) ??
      positiveId(roleId === 2 ? selectedCga : null)
    if (!cga) return null
    // A custom actor's roleEntityId is its custom-user ID, not a company ID.
    if (isCompanyAdminActor && roleId !== 2 && !ca) return null
    if (positiveId(companyAdminId) != null || positiveId(operatorCompanyAdminId) != null) {
      return {companyGroupAdminId: cga, companyAdminId: ca}
    }
    if (ca === cga) {
      return {companyGroupAdminId: cga, companyAdminId: null}
    }
    return {companyGroupAdminId: cga, companyAdminId: ca}
  }

  return null
}

/** Build multipart ownership fields — omits companyAdminId when not genuinely set. */
export function appendCrewOwnershipToFormData(
  formData: FormData,
  ownership: ResolvedCrewOwnership
): void {
  formData.append('companyGroupAdminId', String(ownership.companyGroupAdminId))
  if (ownership.companyAdminId != null && ownership.companyAdminId > 0) {
    formData.append('companyAdminId', String(ownership.companyAdminId))
  }
}
